import { Relationship } from "@/services/backend/relationships/types/relationship";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { Plus } from "lucide-react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useEffect, useState } from "react";
import { v4 } from "uuid";
import { toast } from "sonner";
import {
  AddCommitmentRequest,
  useRelationshipsRelationshipAddCommitmentMutation,
} from "@/services/backend/relationships/api/service";
import t from "@/lang/lang";
import { DateInput } from "@/shared/components/form/date-input";
import { NumberInput } from "@/shared/components/form/number-input";
import {
  inputValidationErrorMessages,
  InputValidationErrors,
} from "@/shared/components/errors/input-error-messages";
import { ServiceKindSelect } from "@/services/backend/services/components/service-kind-select";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { Label } from "@/shared/components/ui/label";
import { useServiceKindList } from "@/services/backend/services/hooks/use-service-kind-list";

export function CreateCommitmentDialog({
  relationship,
}: {
  relationship: Relationship;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(newRequest(relationship));

  const [patch, { isLoading, error, isSuccess, reset }] =
    useRelationshipsRelationshipAddCommitmentMutation();

  useEffect(() => {
    if (isSuccess) {
      setOpen(false);
      setRequest(newRequest(relationship));
      reset();
      toast.success("Sollstellung erstellt");
    }
  }, [isSuccess, relationship, reset]);

  const doCreate = () => {
    if (isLoading) {
      return;
    }

    patch(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <CommonDialogTrigger icon={Plus} label={t("Sollstellung")} size="sm" />
      <DialogContent className="sm:max-w-fit">
        <DialogHeader>
          <DialogTitle>{t("Sollstellung erstellen")}</DialogTitle>
          <DialogDescription>
            {t("Hier kann eine Sollstellung erstellt werden.")}
          </DialogDescription>
        </DialogHeader>
        <CreateCommitmentFields
          request={request}
          onRequestChange={setRequest}
          error={error}
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={doCreate}
          actionLabel={t("Erstellen")}
        />
      </DialogContent>
    </Dialog>
  );
}

export function CreateCommitmentFields({
  request,
  onRequestChange,
  error,
}: {
  request: AddCommitmentRequest;
  onRequestChange: (request: AddCommitmentRequest) => void;
  error: unknown;
}) {
  const serviceKindList = useServiceKindList();

  return (
    <div className="grid gap-4">
      <div className="grid content-start gap-1.5">
        <Label>{t("Dienstleistung")}</Label>
        <ServiceKindSelect
          value={request.serviceKind}
          onValueChange={(serviceKind) =>
            onRequestChange({
              ...request,
              serviceKind: serviceKind as GSMServiceKind,
            })
          }
          availableServiceKinds={serviceKindList as GSMServiceKind[]}
        />
        <InputValidationErrors error={error} field="serviceKind" />
      </div>
      <DateInput
        label={t("Datum")}
        date={request.date}
        onDateChange={(date) =>
          onRequestChange({ ...request, date: date ?? "" })
        }
        hint={t(
          "Das Datum an welchem die Sollstellung ausgeführt werden soll.",
        )}
        errors={inputValidationErrorMessages(error, "date")}
      />
      <NumberInput
        label={t("Turnus (Wochen)")}
        field="cycle"
        min={0}
        number={request.cycle}
        onNumberChange={(cycle) => onRequestChange({ ...request, cycle })}
        errors={inputValidationErrorMessages(error, "cycle")}
      />
    </div>
  );
}

function newRequest(relationship: Relationship): AddCommitmentRequest {
  return {
    id: relationship.id,
    commitmentId: v4(),
    serviceKind: GSMServiceKind.UNDEFINED,
    date: new Date().toISOString(),
    cycle: 0,
  };
}
